<template>
  <div class="root">
    <EventHeader />
    <div class="body">
      <vue-loading v-if="isLoading" type="spiningDubbles" color="#efad4c" :size="{ width: '50px', height: '50px' }" />

      <template v-else>
        <div class="title">{{ $t('eventTheme.title') }}</div>
        <div class="description" v-html="$t('eventTheme.description')"></div>
        <div v-if="items.length">
          <template v-for="item in items">
            <EventThemeItem :item="item" :key="item.id" />
          </template>
        </div>
        <div v-else class="empty-card">{{ $t('eventTheme.noThemes') }}</div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { VueLoading } from "vue-loading-template";
import EventHeader from "@/components/organisms/EventHeader.vue";
import EventThemeItem from "@/components/molecules/EventThemeItem.vue";
import axiosBase from "axios";
import { retryableGet } from "@/lib/utils/networkRetry";
import { compareDesc, format } from "date-fns";

const axiosNola = axiosBase.create({
  baseURL: process.env.VUE_APP_MICROCMS_API_ENDPOINT,
  headers: { "X-API-KEY": process.env.VUE_APP_MICROCMS_API_KEY },
});

const axiosNolaNovel = axiosBase.create({
  baseURL: process.env.VUE_APP_NOLANOVEL_MICROCMS_API_ENDPOINT,
  headers: { "X-API-KEY": process.env.VUE_APP_NOLANOVEL_MICROCMS_API_KEY },
});

export default Vue.extend<Data, Methods, Computed, Props>({
  // NOTE: metaタグの設定
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },
  components: { VueLoading, EventHeader, EventThemeItem },
  data() {
    return {
      isLoading: true,
      items: [],
    };
  },
  async created() {
    const contestRequestUrl = "/contest?filters=isDisplayedByTheme[equals]true";
    const contestRequest = retryableGet(axiosNola, contestRequestUrl);
    const companyRequestUrl = "/company?filters=isDisplayedByTheme[equals]true";
    const companyRequest = retryableGet(axiosNolaNovel, companyRequestUrl);

    const result = await Promise.allSettled([contestRequest, companyRequest]);

    if (result[0].status === "fulfilled")
      this.items = result[0].value.data.contents.map((item: any) => ({ ...item, dataType: "contest" }));
    if (result[1].status === "fulfilled")
      this.items = [
        ...this.items,
        ...result[1].value.data.contents.map((item: any) => ({ ...item, dataType: "company" })),
      ];

    this.items.sort((a, b) => {
      const aPublishedAt = format(a.publishedAt, "YYYY-MM-DD");
      const bPublishedAt = format(b.publishedAt, "YYYY-MM-DD");

      return compareDesc(aPublishedAt, bPublishedAt);
    });

    this.isLoading = false;
  },
});

interface Props {}

interface Data {
  isLoading: boolean;
  items: { [key: string]: any }[];
}

interface Computed {}

interface Methods {}
</script>

<style lang="scss" scoped>
.root {
  height: calc(100vh - #{$header-height});
  background-color: var(--color-background-normal);
  display: flex;
  flex-direction: column;

  @include mq("sp") {
    height: 100%;
    min-height: 0;
  }
}

.body {
  box-sizing: border-box;
  padding: 30px;
  overflow: auto;
}

.title {
  font-weight: var(--font-weight-bold);
  font-size: 1.5em;
  color: var(--color-text-normal);
}

.description {
  font-weight: var(--font-weight-bold);
  font-size: 1.1em;
  margin: 1em 0 2em;
  color: var(--color-text-normal);
}

.empty-card {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 30px;
  font-weight: var(--font-weight-bold);
  font-size: 1.2em;
  color: var(--color-text-normal);
}
</style>
